/**
 * `wayai inbound-webhooks …` — signed endpoints external systems POST into.
 * Top-level (docs/reference/rekor-platform-contracts.md §3.2).
 *
 * The inline mapping block's own inbound-direction key is deliberately not
 * spelled in the help text: it carries the runtime's internal code name, which
 * §3.4 keeps off every integrator-facing surface. The flag still forwards
 * whatever JSON the caller supplies.
 */

import { randomUUID } from 'node:crypto';
import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, parseData, pathSegment, readSecret, requireBase, splitList } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

export function buildInboundWebhooksCommand(): Command {
  const inboundWebhooks = new Command('inbound-webhooks').description(
    'Manage inbound webhook endpoints',
  );

  inboundWebhooks
    .command('create')
    .description('Create a new inbound webhook')
    .requiredOption('--name <name>', 'Inbound webhook name')
    // NOT `--secret <value>`: an argv element is readable from
    // /proc/<pid>/cmdline by any local user on a default `hidepid=0` host, and
    // is recorded in shell history and CI job logs. Whoever reads it can sign
    // forged deliveries into the tenant's base, so the value is read from a
    // pipe or prompted for instead — the same shape `wayai create-credential`
    // already uses.
    .option('--secret-stdin', 'Read the HMAC shared secret from stdin (recommended for CI)')
    .option('--id <id>', 'Inbound webhook ID (auto-generated if omitted)')
    .option('--record-type-scope <record_types>', 'Comma-separated record_type scope')
    .option(
      '--field-mapping <json>',
      'Inline mapping applied to the payload before write — a field_mapping JSON, e.g. {"to_external":{"status":"state"}} (to_external renames auto-invert on read); an explicit inbound-rename block and {"computed":{...}} are also accepted. Mutually exclusive with --source-binding.',
    )
    .option(
      '--source-binding <json>',
      'Reuse a record_type source\'s field_mapping for the inbound translation — {"record_type":"<id>","source":"<name>"}. Mutually exclusive with --field-mapping.',
    )
    .option(
      '--ingest-auth <json>',
      'Inbound auth scheme. Default (omitted) = HMAC signature. Static-header senders: {"type":"static_header","header":"X-Account-Key"} — the configured header is compared to the shared secret (constant-time) instead of an HMAC.',
    )
    .option(
      '--hydration <json>',
      'Make this a reference-style ("notification + fetch") webhook: a thin delivery names a record id; the base fetches the full record via the bound source\'s read endpoint and stores it. Requires --source-binding and a single native --record-type-scope. JSON: {"id_path":"record_id","event_path":"event","event_map":{"ResourceDeleted":"delete"},"default_op":"upsert","merge":true}. Set "merge":true to refresh only the mapped (upstream-owned) fields on re-sync and preserve the locally-owned fields (status/tags/scores) on the same record.',
    )
    .action(async function (
      this: Command,
      opts: {
        name: string;
        secretStdin?: boolean;
        id?: string;
        recordTypeScope?: string;
        fieldMapping?: string;
        sourceBinding?: string;
        ingestAuth?: string;
        hydration?: string;
      },
    ) {
      const secret = await readSecret(
        {
          stdin: opts.secretStdin,
          // No prompt flag here — the prompt is this command's default.
          prompt: undefined,
          stdinFlag: '--secret-stdin',
          promptHint: 'omit --secret-stdin to be prompted',
        },
        'HMAC shared secret',
      );
      const body: Record<string, unknown> = {
        name: opts.name,
        secret,
        enabled: true,
      };
      if (opts.recordTypeScope) body.record_type_scope = splitList(opts.recordTypeScope);
      if (opts.fieldMapping) body.field_mapping = parseData(opts.fieldMapping);
      if (opts.sourceBinding) body.source_binding = parseData(opts.sourceBinding);
      if (opts.ingestAuth) body.ingest_auth = parseData(opts.ingestAuth);
      if (opts.hydration) body.hydration = parseData(opts.hydration);

      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const inboundWebhookId = opts.id ?? randomUUID();
      printOutput(
        await client.request(
          'PUT',
          `/v1/${base}/inbound-webhooks/${pathSegment(inboundWebhookId, '--id')}`,
          body,
        ),
        outputFormat(this),
      );
    });

  inboundWebhooks
    .command('get <id>')
    .description('Get an inbound webhook')
    .action(async function (this: Command, id: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/inbound-webhooks/${pathSegment(id, 'inbound webhook id')}`),
        outputFormat(this),
      );
    });

  inboundWebhooks
    .command('list')
    .description('List all inbound webhooks')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/inbound-webhooks`), outputFormat(this));
    });

  inboundWebhooks
    .command('deliveries')
    .description(
      'List hydration delivery status for reference-style ("notification + fetch") webhooks',
    )
    .option('--status <status>', 'Filter by status (pending|delivered|failed|dead)')
    .option('--inbound-webhook <id>', 'Filter to one inbound webhook')
    .action(async function (this: Command, opts: { status?: string; inboundWebhook?: string }) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const params = new URLSearchParams();
      if (opts.status) params.set('status', opts.status);
      if (opts.inboundWebhook) params.set('inbound_webhook_id', opts.inboundWebhook);
      const qs = params.toString() ? `?${params.toString()}` : '';
      printOutput(
        await client.request('GET', `/v1/${base}/inbound-webhooks/deliveries${qs}`),
        outputFormat(this),
      );
    });

  inboundWebhooks
    .command('delete <id>')
    .description('Delete an inbound webhook')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
      if (!opts.yes && !(await confirm(`Delete inbound webhook ${id}? This cannot be undone.`))) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/inbound-webhooks/${pathSegment(id, 'inbound webhook id')}`);
      console.log('Deleted');
    });

  return inboundWebhooks;
}
